import React, { useState } from "react";
import { useGetArchivedTenderApiQuery } from "../../redux/services/apiSlice";
import Loader from "../../components/Loader";

import TenderSearchBar from "../../components/TenderSearchBar";
import InnerBannerBreadcrumb from "../../components/shared/breadcrumb/InnerBannerBreadcrumb";
import { useLanguageTranslation } from "../../hooks/useLanguageTranslation";
import { useSelector } from "react-redux";
import PaginationNew from "../../components/Pagination/PaginationNew";
import PageLastUpdated from "../../components/shared/PageLastUpdated";
import { useTableResponsive } from "../../hooks/useTableResponsive";
import useSeo from "../../hooks/useSeo";
import { useLocation } from "react-router-dom";
import PdfZipItem from "../../components/PdfZipItem";
import { convertDateFormat } from "../../Utils/utils";

const ArchivedTenders = () => {
  const { getText } = useLanguageTranslation();
  const { languageType } = useSelector((state) => state.homeSlice);
  const [currentPage, setCurrentPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const {
    data: archivedTendersData = [],
    isLoading: archivedTendersIsLoading,
    isFetching: archivedTendersIsFetching,
  } = useGetArchivedTenderApiQuery({
    page: currentPage,
    title: searchTerm,
    language: languageType,
  });

  useTableResponsive(archivedTendersData);

  const lastUpdatedOn = archivedTendersData?.tender_data?.[0]?.changed_date;

  const totalPages = archivedTendersData?.total_pages - 1;

  const handleSubmitSearch = (inputText) => {
    setCurrentPage("");
    setSearchTerm(inputText);
  };

  // for SEO
  const { pathname } = useLocation();
  const { innerBannerImage, innerBannerTitle } = useSeo({ pathname });

  if (archivedTendersIsLoading || archivedTendersIsFetching) {
    return <Loader />;
  }

  return (
    <>
      <InnerBannerBreadcrumb
        title={getText("Tender", "निविदा")}
        titleLink="/tender/archive-tender"
        levelTwo={true}
        levelThree={false}
        bannerImage={innerBannerImage}
        titleTwo={innerBannerTitle}
        isActive={innerBannerTitle}
      />

      <div className="inner-container">
        <div className="container">
          <div className="container">
            <div className="row">
              <div className="col-12 col-sm-12 col-md-6 col-lg-6 d-flex justify-content-between mb-3">
                <TenderSearchBar
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  title={getText("Tender Number", "निविदा संख्या")}
                  setCurrentPage={setCurrentPage}
                  handleSubmitSearch={handleSubmitSearch}
                />
              </div>
              {/* <div className="col-12 col-sm-12 col-md-6 col-lg-6 d-flex justify-content-end mb-3">
                <PageLastUpdated lastUpdatedOn={lastUpdatedOn} />
              </div> */}
              <div className="col-12 col-sm-12 col-md-12 col-lg-12">
                <div className="table-responsive">
                  <table className="table table-striped custom-table">
                    <thead className="thead-dark">
                      <tr>
                        <th>{getText("S.No.", "क्र. सं.")}</th>
                        <th>{getText("Tender Number", "निविदा संख्या")}</th>
                        <th>{getText("Tender Date", "निविदा दिनांक")}</th>
                        <th>
                          {getText(
                            "Short Notice Inviting Bids",
                            "संक्षिप्त सूचना बोली निविदाएं",
                          )}
                        </th>
                        <th>{getText("Corrigendum", "शुद्धिपत्र")}</th>
                        <th>
                          {getText(
                            "Other Tender Documents",
                            "अन्य निविदा दस्तावेज़",
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {archivedTendersData?.tender_data?.length > 0 ? (
                        archivedTendersData?.tender_data?.map((item, index) => (
                          <tr key={index}>
                            <td data-key={getText("S.No.", "क्र. सं.")}>
                              {item.count}
                            </td>
                            <td
                              data-key={getText(
                                "Tender Number",
                                "निविदा संख्या",
                              )}
                            >
                              <PdfZipItem
                                key={index}
                                filePath={item?.uri}
                                title={item.title}
                                filesize={item.file_size}
                              />
                            </td>
                            <td
                              className="no-wrap-td"
                              data-key={getText("Tender Date", "निविदा दिनांक")}
                            >
                              {convertDateFormat(item.field_tender_date_value)}
                            </td>
                            <td
                              data-key={getText(
                                "Short Notice Inviting Bids",
                                "संक्षिप्त सूचना बोली निविदाएं",
                              )}
                            >
                              <>
                                <a
                                  key={index}
                                  href={item.uri}
                                  target="_blank" rel="noopener noreferrer"
                                  className="mr-2"
                                >
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html:
                                        item.field_short_notice_inviting_bids_value,
                                    }}
                                  ></div>
                                </a>
                                {item.file_size && (
                                  <span>({item.file_size})</span>
                                )}
                              </>
                            </td>
                            <td
                              className="no-wrap-td"
                              data-key={getText("Corrigendum", "शुद्धिपत्र")}
                            >
                              {/* corrigendum */}
                              {item.corrigendum &&
                                item?.corrigendum?.files?.map((subitem, i) => (
                                  <PdfZipItem
                                    key={i}
                                    filePath={subitem?.corrigendum_uri}
                                    title={subitem.corrigendum_file_description}
                                    filesize={item.file_size}
                                  />
                                ))}
                            </td>
                            <td
                              className="no-wrap-td"
                              data-key={getText(
                                "Other Tender Documents",
                                "अन्य निविदा दस्तावेज़",
                              )}
                            >
                              {/* techno commercial bids */}
                              <div className="my-2">
                                {item.tender_details_of_techno_commerc &&
                                  item?.tender_details_of_techno_commerc?.files?.map(
                                    (subitem, i) => (
                                      <PdfZipItem
                                        key={i}
                                        filePath={subitem?.file_uri}
                                        title={subitem.file_description}
                                        filesize={subitem.file_size}
                                      />
                                    ),
                                  )}
                              </div>
                              {/* successful bidders */}
                              <div className="my-2">
                                {item.sucessful_bidder &&
                                  item?.sucessful_bidder?.files?.map(
                                    (subitem, i) => (
                                      <PdfZipItem
                                        key={i}
                                        filePath={subitem?.file_uri}
                                        title={subitem.file_description}
                                        filesize={subitem.file_size}
                                      />
                                    ),
                                  )}
                              </div>

                              {/* clarification  */}
                              <div className="my-2">
                                {item.clarification &&
                                  item?.clarification?.files?.map(
                                    (subitem, i) => (
                                      <PdfZipItem
                                        key={i}
                                        filePath={subitem?.clarification_uri}
                                        title={subitem.file_description}
                                        filesize={subitem.file_size}
                                      />
                                    ),
                                  )}
                              </div>
                              {/* detailed bid document */}
                              {item.detailed_bid_documents &&
                                item?.detailed_bid_documents?.files?.map(
                                  (subitem, i) => (
                                    <PdfZipItem
                                      key={i}
                                      filePath={subitem?.bid_uri}
                                      title={subitem.file_description}
                                      filesize={subitem.file_size}
                                    />
                                  ),
                                )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No records found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <PaginationNew
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                  totalPages={totalPages}
                />
              </div>
              <PageLastUpdated lastUpdatedOn={lastUpdatedOn} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ArchivedTenders;
